import React, {useState} from "react";
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    ReferenceLine,
    BarChart,
    Bar,
    RadarChart,
    Radar,
    PolarGrid,
    PolarAngleAxis,
    PolarRadiusAxis,
    AreaChart,
    Area
} from "recharts";

// ─── CONFIG ──────────────────────────────────────────────────────────────────

const PROF = 70;
const SKILLS = {
    reason: {name: "Reasoning", short: "Reason", color: "#2563EB"},
    construct_arguments: {name: "Construct Arguments", short: "Argue", color: "#059669"},
    precision: {name: "Precision", short: "Precise", color: "#D97706"},
    model_analyze: {name: "Model & Analyze", short: "Model", color: "#7C3AED"},
    communicate: {name: "Communication", short: "Comm", color: "#DB2777"},
    grit: {name: "Grit", short: "Grit", color: "#DC2626"},
};
const SK_KEYS = Object.keys(SKILLS);

// ─── DISTRICT DATA ───────────────────────────────────────────────────────────

const DISTRICT = {
    name: "Riverside Unified School District",
    superintendent: "Dr. Maria Santos",
    schools: 8,
    students: 3840,
    teachers: 192,
    grades_served: "3–6",
    challenges_per_grade: 12,
    challenges_total: 48,
    year: "2025–2026",
};

const SCHOOLS = [
    {
        id: "lincoln", name: "Lincoln Elementary", students: 480, teachers: 24, title1: true,
        scores: {reason: 65, construct_arguments: 54, precision: 80, model_analyze: 57, communicate: 72, grit: 75},
        growth: {reason: 17, construct_arguments: 16, precision: 18, model_analyze: 15, communicate: 14, grit: 20},
        proficiency: {exceeded: 62, met: 178, nearly: 152, did_not: 88},
    },
    {
        id: "washington", name: "Washington Elementary", students: 520, teachers: 26, title1: true,
        scores: {reason: 58, construct_arguments: 48, precision: 74, model_analyze: 50, communicate: 65, grit: 68},
        growth: {reason: 12, construct_arguments: 10, precision: 14, model_analyze: 8, communicate: 10, grit: 15},
        proficiency: {exceeded: 42, met: 168, nearly: 194, did_not: 116},
    },
    {
        id: "jefferson", name: "Jefferson Elementary", students: 440, teachers: 22, title1: false,
        scores: {reason: 72, construct_arguments: 62, precision: 85, model_analyze: 65, communicate: 78, grit: 80},
        growth: {reason: 14, construct_arguments: 18, precision: 12, model_analyze: 16, communicate: 10, grit: 12},
        proficiency: {exceeded: 88, met: 198, nearly: 110, did_not: 44},
    },
    {
        id: "adams", name: "Adams Elementary", students: 510, teachers: 26, title1: true,
        scores: {reason: 55, construct_arguments: 44, precision: 70, model_analyze: 46, communicate: 62, grit: 64},
        growth: {reason: 8, construct_arguments: 6, precision: 10, model_analyze: 5, communicate: 8, grit: 10},
        proficiency: {exceeded: 36, met: 148, nearly: 198, did_not: 128},
    },
    {
        id: "madison", name: "Madison Elementary", students: 480, teachers: 24, title1: false,
        scores: {reason: 70, construct_arguments: 60, precision: 82, model_analyze: 62, communicate: 76, grit: 78},
        growth: {reason: 15, construct_arguments: 14, precision: 16, model_analyze: 14, communicate: 12, grit: 14},
        proficiency: {exceeded: 72, met: 192, nearly: 140, did_not: 76},
    },
    {
        id: "monroe", name: "Monroe Elementary", students: 390, teachers: 20, title1: true,
        scores: {reason: 52, construct_arguments: 42, precision: 68, model_analyze: 44, communicate: 60, grit: 62},
        growth: {reason: 6, construct_arguments: 4, precision: 8, model_analyze: 3, communicate: 6, grit: 8},
        proficiency: {exceeded: 24, met: 108, nearly: 156, did_not: 102},
    },
    {
        id: "roosevelt", name: "Roosevelt Elementary", students: 540, teachers: 27, title1: false,
        scores: {reason: 68, construct_arguments: 58, precision: 84, model_analyze: 60, communicate: 74, grit: 76},
        growth: {reason: 16, construct_arguments: 15, precision: 14, model_analyze: 13, communicate: 11, grit: 16},
        proficiency: {exceeded: 76, met: 210, nearly: 162, did_not: 92},
    },
    {
        id: "kennedy", name: "Kennedy Elementary", students: 480, teachers: 23, title1: false,
        scores: {reason: 66, construct_arguments: 56, precision: 78, model_analyze: 58, communicate: 70, grit: 72},
        growth: {reason: 13, construct_arguments: 12, precision: 11, model_analyze: 11, communicate: 10, grit: 12},
        proficiency: {exceeded: 58, met: 182, nearly: 156, did_not: 84},
    },
];

const DISTRICT_TREND = [
    {month: "Sep", reason: 46, construct_arguments: 36, precision: 62, model_analyze: 40, communicate: 56, grit: 50},
    {month: "Oct", reason: 50, construct_arguments: 40, precision: 66, model_analyze: 43, communicate: 59, grit: 54},
    {month: "Nov", reason: 53, construct_arguments: 43, precision: 70, model_analyze: 46, communicate: 62, grit: 58},
    {month: "Jan", reason: 57, construct_arguments: 46, precision: 74, model_analyze: 49, communicate: 65, grit: 64},
    {month: "Feb", reason: 60, construct_arguments: 49, precision: 77, model_analyze: 52, communicate: 68, grit: 68},
    {month: "Mar", reason: 63, construct_arguments: 53, precision: 80, model_analyze: 55, communicate: 71, grit: 72},
];

const EQUITY_DATA = [
    {group: "All Students", reason: 63, construct_arguments: 53, precision: 80, model_analyze: 55, n: 3840},
    {group: "Title I Schools", reason: 57, construct_arguments: 47, precision: 73, model_analyze: 49, n: 1900},
    {group: "Non-Title I", reason: 69, construct_arguments: 59, precision: 82, model_analyze: 61, n: 1940},
    {group: "ELL Students", reason: 50, construct_arguments: 40, precision: 70, model_analyze: 42, n: 845},
    {group: "SpEd Students", reason: 44, construct_arguments: 35, precision: 62, model_analyze: 38, n: 576},
];

const GRADE_BAND = [
    {grade: "Grade 3", reason: 56, construct_arguments: 44, precision: 72, model_analyze: 46, students: 960},
    {grade: "Grade 4", reason: 63, construct_arguments: 53, precision: 80, model_analyze: 55, students: 960},
    {grade: "Grade 5", reason: 68, construct_arguments: 58, precision: 83, model_analyze: 60, students: 960},
    {grade: "Grade 6", reason: 72, construct_arguments: 62, precision: 86, model_analyze: 65, students: 960},
];

// ─── STRATEGIC RECOMMENDATIONS ───────────────────────────────────────────────

const STRATEGIC = [
    {
        priority: "critical", color: "#DC2626",
        title: "Argument Quality Is the District's Structural Weakness",
        metric: "53% district average — lowest skill, 27 pts below Precision",
        insight: "Constructing Arguments is the lowest-performing skill across all 8 schools, all 4 grade levels, and every demographic subgroup. The gap between Precision (80%) and Arguments (53%) is 27 points — the largest spread in the entire framework. This is not a school effect or a teacher effect. It is a systemic instructional gap: the district has built strong procedural fluency but has not invested equivalently in mathematical argumentation. Every school shows the same pattern, which means the issue is upstream of individual teachers.",
        action: "District-wide Claim-Evidence-Reasoning (CER) adoption across grades 3–6 for the 2026–27 school year. Allocate PD budget accordingly: 2 full-day trainings in August, monthly coaching cycles through December. Target: close the Precision-Arguments gap from 27 pts to 15 pts by March 2027.",
        budget_signal: "Estimated investment: $48K (trainer fees + sub coverage for PD days). ROI benchmark: if Arguments average reaches 65% (+12 pts), district meets Portrait of a Graduate argumentation milestone.",
    },
    {
        priority: "critical", color: "#DC2626",
        title: "Monroe and Adams Require Targeted Support — Growth Stalling",
        metric: "Monroe: +3 to +8 pts growth | Adams: +5 to +10 pts growth (district avg: +11 to +18 pts)",
        insight: "Monroe and Adams are growing at roughly half the rate of the other six schools. Both are Title I schools, but Washington (also Title I) is growing at near-district average rates, so Title I status alone does not explain the gap. Monroe shows the lowest absolute growth in Model & Analyze (+3 pts) and Constructing Arguments (+4 pts) — these are effectively flat lines over 6 months. Adams shows a similar pattern but slightly less severe. Without intervention, these schools will diverge further from district averages, widening the equity gap.",
        action: "Deploy instructional coaching support to Monroe and Adams for the remainder of the year. Model on Washington's trajectory: Washington is Title I but growing at +10 to +15 pts, suggesting effective instructional practices that can transfer. Pair Monroe/Adams teachers with Washington mentor teachers for bi-weekly lesson study cycles.",
        budget_signal: "Estimated investment: $32K (0.5 FTE instructional coach redeployed for 4 months). ROI benchmark: if Monroe/Adams growth rates match Washington's by June, the district closes a 12-point equity gap.",
    },
    {
        priority: "high", color: "#D97706",
        title: "Title I / Non-Title I Skill Gap Is Narrowing for Precision but Widening for Arguments",
        metric: "Precision gap: 9 pts (73% vs 82%) | Arguments gap: 12 pts (47% vs 59%)",
        insight: "The equity story is mixed. Precision is the most equitable skill: the Title I / Non-Title I gap is just 9 points and has narrowed by 3 points since September. This suggests that procedural skill instruction translates well across contexts. However, Constructing Arguments shows a 12-point gap that has widened by 2 points since September. The skill that requires the most language-intensive reasoning is also the skill with the largest equity gap. ELL students show an even wider gap (40% vs. 53% district average).",
        action: "Embed language scaffolding into argument instruction — sentence frames, vocabulary support, and oral rehearsal before written responses. This benefits all students but disproportionately supports ELL and Title I populations. Partner with ELL coordinator to develop math-specific argument language supports.",
        budget_signal: "Minimal incremental cost — primarily integration into existing CER PD. Add $6K for ELL-specific materials development.",
    },
    {
        priority: "high", color: "#D97706",
        title: "Model & Analyze: District-Wide Growth Is Decelerating",
        metric: "55% average, +15 pts YTD — but only +3 pts in last 2 months",
        insight: "Model & Analyze grew steadily from September through January (+15 pts in 4 months) but has nearly stalled since (+3 pts in 2 months). This pattern appears across 6 of 8 schools, suggesting a curriculum ceiling rather than an instructional issue. Students can perform mathematical operations and make simple predictions, but the challenges may not be scaffolding the deeper analytical reasoning required to move past 55%.",
        action: "Curriculum team review: audit challenge designs for Model & Analyze scaffolding. Consider adding explicit 'bridge prompts' between computation steps and real-world prediction steps. Pilot redesigned challenges at Jefferson and Roosevelt (highest current scores) to test whether design changes unlock further growth.",
        budget_signal: "Internal curriculum team time. No incremental budget required.",
    },
    {
        priority: "monitor", color: "#2563EB",
        title: "Jefferson Is the Bright Spot — Study What's Working",
        metric: "Highest scores in 5 of 6 skills. Strongest growth in Arguments (+18 pts).",
        insight: "Jefferson outperforms the district average on every skill and shows the strongest growth in the district's weakest area (Arguments: +18 pts vs. +16 district average). Jefferson is not Title I, so some advantage is contextual, but the Arguments growth rate exceeds even other non-Title I schools (Madison: +14, Kennedy: +12). Something instructional is working at Jefferson specifically for argumentation skill development.",
        action: "Conduct an instructional audit at Jefferson focused on how teachers facilitate explanation questions. Document practices for district-wide dissemination. Consider Jefferson as the pilot site for the CER framework rollout.",
        budget_signal: "Minimal — 2 days of instructional coaching time for observation and documentation.",
    },
];

// ─── COMPONENTS ──────────────────────────────────────────────────────────────

const CustomTooltip = ({active, payload, label}) => {
    if (!active || !payload) return null;
    return (
        <div style={{
            background: "#111827",
            padding: "10px 14px",
            borderRadius: "8px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.25)"
        }}>
            <div style={{fontSize: "11px", fontWeight: 700, color: "#9CA3AF", marginBottom: "5px"}}>{label}</div>
            {payload.map((p, i) => (
                <div key={i}
                     style={{display: "flex", justifyContent: "space-between", gap: "14px", marginBottom: "1px"}}>
                    <span style={{fontSize: "11px", color: p.color, fontWeight: 600}}>{p.name}</span>
                    <span style={{
                        fontSize: "11px",
                        color: "#F9FAFB",
                        fontWeight: 700
                    }}>{typeof p.value === "number" ? `${p.value}%` : p.value}</span>
                </div>
            ))}
        </div>
    );
};

const MetricCard = ({label, value, sub, accent}) => (
    <div style={{
        padding: "16px 18px", background: "#FFF", borderRadius: "10px",
        border: "1px solid #E5E7EB", borderTop: `3px solid ${accent || "#E5E7EB"}`,
    }}>
        <div style={{
            fontSize: "10px",
            fontWeight: 700,
            color: "#9CA3AF",
            textTransform: "uppercase",
            letterSpacing: "0.8px",
            marginBottom: "4px"
        }}>{label}</div>
        <div style={{fontSize: "24px", fontWeight: 800, color: "#111827", lineHeight: 1.1}}>{value}</div>
        {sub && <div style={{fontSize: "11px", color: "#6B7280", marginTop: "3px"}}>{sub}</div>}
    </div>
);

const SchoolRow = ({school, districtAvg, expanded, onToggle}) => {
    const avgScore = Math.round(SK_KEYS.reduce((s, k) => s + school.scores[k], 0) / SK_KEYS.length);
    const avgGrowth = Math.round(SK_KEYS.reduce((s, k) => s + school.growth[k], 0) / SK_KEYS.length);
    const totalProf = school.proficiency.exceeded + school.proficiency.met;
    const profPct = Math.round((totalProf / school.students) * 100);
    const lowGrowth = avgGrowth < 10;
    return (
        <div style={{
            background: "#FFF", borderRadius: "10px", border: "1px solid #E5E7EB", overflow: "hidden",
            boxShadow: expanded ? "0 2px 12px rgba(0,0,0,0.06)" : "none",
            borderLeft: lowGrowth ? "4px solid #DC2626" : "4px solid transparent",
        }}>
            <div onClick={onToggle}
                 style={{padding: "14px 18px", cursor: "pointer", display: "flex", alignItems: "center", gap: "14px"}}>
                <div style={{flex: 1}}>
                    <div style={{display: "flex", alignItems: "center", gap: "8px", marginBottom: "2px"}}>
                        <span style={{fontSize: "14px", fontWeight: 700, color: "#111827"}}>{school.name}</span>
                        {school.title1 && <span style={{
                            fontSize: "9px",
                            fontWeight: 700,
                            padding: "1px 6px",
                            borderRadius: "3px",
                            background: "#EFF6FF",
                            color: "#1E40AF"
                        }}>TITLE I</span>}
                        {lowGrowth && <span style={{
                            fontSize: "9px",
                            fontWeight: 700,
                            padding: "1px 6px",
                            borderRadius: "3px",
                            background: "#FEE2E2",
                            color: "#991B1B"
                        }}>LOW GROWTH</span>}
                    </div>
                    <div style={{fontSize: "11px", color: "#6B7280"}}>{school.students} students
                        · {school.teachers} teachers
                    </div>
                </div>
                <div style={{textAlign: "center", minWidth: "60px"}}>
                    <div style={{
                        fontSize: "18px",
                        fontWeight: 800,
                        color: avgScore >= PROF ? "#059669" : "#D97706"
                    }}>{avgScore}%
                    </div>
                    <div style={{fontSize: "9px", color: "#9CA3AF", textTransform: "uppercase"}}>Avg Score</div>
                </div>
                <div style={{textAlign: "center", minWidth: "60px"}}>
                    <div style={{
                        fontSize: "18px",
                        fontWeight: 800,
                        color: avgGrowth >= 12 ? "#059669" : avgGrowth >= 8 ? "#D97706" : "#DC2626"
                    }}>+{avgGrowth}</div>
                    <div style={{fontSize: "9px", color: "#9CA3AF", textTransform: "uppercase"}}>Avg Growth</div>
                </div>
                <div style={{textAlign: "center", minWidth: "60px"}}>
                    <div style={{
                        fontSize: "18px",
                        fontWeight: 800,
                        color: profPct >= 60 ? "#059669" : "#D97706"
                    }}>{profPct}%
                    </div>
                    <div style={{fontSize: "9px", color: "#9CA3AF", textTransform: "uppercase"}}>Proficient</div>
                </div>
                <span style={{
                    fontSize: "16px",
                    color: "#9CA3AF",
                    transform: expanded ? "rotate(180deg)" : "rotate(0)",
                    transition: "transform 0.2s"
                }}>▾</span>
            </div>
            {expanded && (
                <div style={{padding: "0 18px 18px", borderTop: "1px solid #F3F4F6"}}>
                    <div
                        style={{display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "6px", marginTop: "12px"}}>
                        {SK_KEYS.map(sk => {
                            const s = school.scores[sk];
                            const g = school.growth[sk];
                            const da = districtAvg[sk];
                            const diff = s - da;
                            return (
                                <div key={sk}
                                     style={{padding: "10px 12px", background: "#F9FAFB", borderRadius: "6px"}}>
                                    <div style={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        marginBottom: "4px"
                                    }}>
                                        <span style={{
                                            fontSize: "11px",
                                            fontWeight: 600,
                                            color: SKILLS[sk].color
                                        }}>{SKILLS[sk].short}</span>
                                        <span style={{
                                            fontSize: "9px",
                                            fontWeight: 600,
                                            color: diff >= 0 ? "#059669" : "#DC2626"
                                        }}>
                      {diff >= 0 ? "+" : ""}{diff} vs dist.
                    </span>
                                    </div>
                                    <div style={{display: "flex", alignItems: "baseline", gap: "6px"}}>
                                        <span style={{fontSize: "16px", fontWeight: 800, color: "#111827"}}>{s}%</span>
                                        <span style={{
                                            fontSize: "11px",
                                            fontWeight: 700,
                                            color: g >= 12 ? "#059669" : g >= 8 ? "#D97706" : "#DC2626"
                                        }}>+{g} pts</span>
                                    </div>
                                    <div style={{
                                        marginTop: "4px",
                                        height: "4px",
                                        borderRadius: "2px",
                                        background: "#E5E7EB",
                                        overflow: "hidden"
                                    }}>
                                        <div style={{
                                            width: `${s}%`,
                                            height: "100%",
                                            borderRadius: "2px",
                                            background: SKILLS[sk].color
                                        }}/>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    {/* Proficiency breakdown */}
                    <div style={{display: "flex", gap: "8px", marginTop: "10px", alignItems: "center"}}>
                        <span style={{fontSize: "10px", fontWeight: 700, color: "#6B7280"}}>Proficiency:</span>
                        {[
                            {label: "Exceeded", n: school.proficiency.exceeded, bg: "#ECFDF5", c: "#065F46"},
                            {label: "Met", n: school.proficiency.met, bg: "#EFF6FF", c: "#1E40AF"},
                            {label: "Nearly", n: school.proficiency.nearly, bg: "#FFFBEB", c: "#92400E"},
                            {label: "DNM", n: school.proficiency.did_not, bg: "#FEF2F2", c: "#991B1B"},
                        ].map(d => (
                            <div key={d.label} style={{display: "flex", alignItems: "center", gap: "4px"}}>
                                <span style={{
                                    fontSize: "11px",
                                    fontWeight: 700,
                                    padding: "2px 6px",
                                    borderRadius: "3px",
                                    background: d.bg,
                                    color: d.c
                                }}>{d.n}</span>
                                <span style={{fontSize: "9px", color: "#9CA3AF"}}>{d.label}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

// ─── MAIN ────────────────────────────────────────────────────────────────────

export default function DistrictDashboard() {
    const [section, setSection] = useState("overview");
    const [expandedSchool, setExpandedSchool] = useState("monroe");
    const [showJson, setShowJson] = useState(false);

    const districtAvg = {};
    SK_KEYS.forEach(sk => {
        districtAvg[sk] = Math.round(SCHOOLS.reduce((s, sc) => s + sc.scores[sk], 0) / SCHOOLS.length);
    });

    const districtGrowthAvg = {};
    SK_KEYS.forEach(sk => {
        districtGrowthAvg[sk] = Math.round(SCHOOLS.reduce((s, sc) => s + sc.growth[sk], 0) / SCHOOLS.length);
    });

    const totalProficient = SCHOOLS.reduce((s, sc) => s + sc.proficiency.exceeded + sc.proficiency.met, 0);
    const totalDNM = SCHOOLS.reduce((s, sc) => s + sc.proficiency.did_not, 0);

    const radarData = SK_KEYS.map(sk => ({
        skill: SKILLS[sk].short,
        district: districtAvg[sk],
        topSchool: Math.max(...SCHOOLS.map(s => s.scores[sk])),
        bottomSchool: Math.min(...SCHOOLS.map(s => s.scores[sk])),
    }));

    const tab = (label, val) => ({
        padding: "7px 16px", borderRadius: "7px", border: "none", cursor: "pointer",
        fontSize: "12px", fontWeight: 600, transition: "all 0.15s",
        background: section === val ? "#111827" : "transparent",
        color: section === val ? "#FFF" : "#6B7280",
    });

    const jsonOutput = {
        district: DISTRICT,
        reporting_period: "September 2025 — March 2026",
        district_averages: districtAvg,
        district_growth: districtGrowthAvg,
        proficiency_summary: {
            total_students: DISTRICT.students,
            proficient_or_above: totalProficient,
            proficient_pct: Math.round((totalProficient / DISTRICT.students) * 100),
            did_not_meet: totalDNM,
        },
        schools: SCHOOLS,
        equity_analysis: EQUITY_DATA,
        grade_progression: GRADE_BAND,
        growth_trajectory: DISTRICT_TREND,
        strategic_recommendations: STRATEGIC,
    };

    return (
        <div style={{
            fontFamily: "'Instrument Sans', 'DM Sans', 'Segoe UI', system-ui, sans-serif",
            background: "#F7F8FA", minHeight: "100vh",
        }}>
            {/* Header */}
            <div style={{
                background: "linear-gradient(135deg, #0F172A 0%, #1E293B 100%)",
                padding: "28px 32px 22px", borderBottom: "3px solid #D97706",
            }}>
                <div style={{display: "flex", justifyContent: "space-between", alignItems: "flex-start"}}>
                    <div>
                        <div style={{
                            fontSize: "10px",
                            fontWeight: 700,
                            letterSpacing: "2px",
                            textTransform: "uppercase",
                            color: "#FCD34D",
                            marginBottom: "6px"
                        }}>
                            Mindsets Intelligence Engine — District Dashboard
                        </div>
                        <h1 style={{
                            margin: 0,
                            fontSize: "22px",
                            fontWeight: 700,
                            color: "#F9FAFB",
                            letterSpacing: "-0.3px"
                        }}>
                            {DISTRICT.name}
                        </h1>
                        <div style={{fontSize: "12px", color: "#94A3B8", marginTop: "4px"}}>
                            {DISTRICT.superintendent} · {DISTRICT.schools} schools
                            · {DISTRICT.students.toLocaleString()} students ·
                            Grades {DISTRICT.grades_served} · {DISTRICT.year}
                        </div>
                    </div>
                    <div style={{textAlign: "right"}}>
                        <div style={{
                            fontSize: "10px",
                            fontWeight: 700,
                            color: "#94A3B8",
                            textTransform: "uppercase",
                            marginBottom: "4px"
                        }}>Challenges per Grade
                        </div>
                        <div style={{
                            fontSize: "26px",
                            fontWeight: 800,
                            color: "#FCD34D",
                            lineHeight: 1
                        }}>{DISTRICT.challenges_per_grade}</div>
                        <div style={{
                            fontSize: "11px",
                            color: "#94A3B8",
                            marginTop: "3px"
                        }}>{DISTRICT.challenges_total} across {DISTRICT.grades_served}</div>
                    </div>
                </div>
            </div>

            {/* Nav */}
            <div style={{
                padding: "12px 32px", display: "flex", alignItems: "center", gap: "12px",
                borderBottom: "1px solid #E2E8F0", background: "#FFF", flexWrap: "wrap",
            }}>
                <div style={{display: "flex", gap: "3px", background: "#F1F5F9", padding: "3px", borderRadius: "9px"}}>
                    <button style={tab("Overview", "overview")} onClick={() => setSection("overview")}>Overview</button>
                    <button style={tab("Schools", "schools")} onClick={() => setSection("schools")}>Schools</button>
                    <button style={tab("Equity", "equity")} onClick={() => setSection("equity")}>Equity</button>
                    <button style={tab("Strategy", "strategy")} onClick={() => setSection("strategy")}>Strategic Recs
                    </button>
                </div>
                <button onClick={() => setShowJson(!showJson)} style={{
                    marginLeft: "auto", padding: "6px 14px", borderRadius: "6px", border: "1px solid #CBD5E1",
                    background: showJson ? "#111827" : "#FFF", color: showJson ? "#FFF" : "#374151",
                    fontSize: "11px", fontWeight: 600, cursor: "pointer",
                }}>{showJson ? "◉" : "○"} JSON
                </button>
            </div>

            <div style={{padding: "20px 32px", maxWidth: "1040px"}}>

                {/* ════════ OVERVIEW ════════ */}
                {section === "overview" && (
                    <>
                        {/* KPI Row */}
                        <div style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(4, 1fr)",
                            gap: "10px",
                            marginBottom: "18px"
                        }}>
                            <MetricCard label="District Avg. Score"
                                        value={`${Math.round(SK_KEYS.reduce((s, k) => s + districtAvg[k], 0) / SK_KEYS.length)}%`}
                                        sub="Across all 6 skills" accent="#2563EB"/>
                            <MetricCard label="Students Proficient"
                                        value={`${Math.round((totalProficient / DISTRICT.students) * 100)}%`}
                                        sub={`${totalProficient.toLocaleString()} of ${DISTRICT.students.toLocaleString()}`}
                                        accent="#059669"/>
                            <MetricCard label="Avg. YTD Growth"
                                        value={`+${Math.round(SK_KEYS.reduce((s, k) => s + districtGrowthAvg[k], 0) / SK_KEYS.length)} pts`}
                                        sub="September → March" accent="#7C3AED"/>
                            <MetricCard label="Students Below Prof." value={totalDNM.toLocaleString()}
                                        sub={`${Math.round((totalDNM / DISTRICT.students) * 100)}% of district`}
                                        accent="#DC2626"/>
                        </div>

                        {/* Growth trajectory */}
                        <div style={{
                            background: "#FFF",
                            borderRadius: "10px",
                            border: "1px solid #E2E8F0",
                            padding: "20px 16px 10px 0",
                            marginBottom: "18px"
                        }}>
                            <div style={{
                                fontSize: "11px",
                                fontWeight: 700,
                                color: "#64748B",
                                textTransform: "uppercase",
                                letterSpacing: "0.8px",
                                marginBottom: "10px",
                                paddingLeft: "20px"
                            }}>
                                District Skill Trajectories — All Schools, All Grades
                            </div>
                            <ResponsiveContainer width="100%" height={260}>
                                <LineChart data={DISTRICT_TREND} margin={{top: 5, right: 20, bottom: 5, left: 10}}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9"/>
                                    <XAxis dataKey="month" tick={{fontSize: 11, fill: "#64748B"}}/>
                                    <YAxis domain={[0, 100]} tick={{fontSize: 11, fill: "#64748B"}}
                                           tickFormatter={v => `${v}%`}/>
                                    <Tooltip content={<CustomTooltip/>}/>
                                    <ReferenceLine y={PROF} stroke="#94A3B8" strokeDasharray="6 4" label={{
                                        value: "Proficient",
                                        position: "right",
                                        fontSize: 10,
                                        fill: "#94A3B8"
                                    }}/>
                                    {SK_KEYS.map(sk => (
                                        <Line key={sk} type="monotone" dataKey={sk} name={SKILLS[sk].short}
                                              stroke={SKILLS[sk].color} strokeWidth={2.5}
                                              dot={{r: 3.5, fill: SKILLS[sk].color}}/>
                                    ))}
                                </LineChart>
                            </ResponsiveContainer>
                        </div>

                        {/* Radar + Grade Band side by side */}
                        <div style={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr",
                            gap: "12px",
                            marginBottom: "18px"
                        }}>
                            <div style={{
                                background: "#FFF",
                                borderRadius: "10px",
                                border: "1px solid #E2E8F0",
                                padding: "18px"
                            }}>
                                <div style={{
                                    fontSize: "11px",
                                    fontWeight: 700,
                                    color: "#64748B",
                                    textTransform: "uppercase",
                                    letterSpacing: "0.8px",
                                    marginBottom: "6px"
                                }}>
                                    Skill Profile — District vs. Range
                                </div>
                                <ResponsiveContainer width="100%" height={240}>
                                    <RadarChart data={radarData}>
                                        <PolarGrid stroke="#E2E8F0"/>
                                        <PolarAngleAxis dataKey="skill" tick={{fontSize: 10, fill: "#64748B"}}/>
                                        <PolarRadiusAxis domain={[0, 100]} tick={{fontSize: 9, fill: "#94A3B8"}}/>
                                        <Radar name="Top School" dataKey="topSchool" stroke="#059669" fill="#059669"
                                               fillOpacity={0.1} strokeWidth={1.5}/>
                                        <Radar name="District Avg" dataKey="district" stroke="#2563EB" fill="#2563EB"
                                               fillOpacity={0.15} strokeWidth={2.5}/>
                                        <Radar name="Bottom School" dataKey="bottomSchool" stroke="#DC2626"
                                               fill="#DC2626" fillOpacity={0.08} strokeWidth={1.5}
                                               strokeDasharray="4 4"/>
                                    </RadarChart>
                                </ResponsiveContainer>
                                <div style={{display: "flex", justifyContent: "center", gap: "16px", marginTop: "4px"}}>
                                    {[{label: "Top School", c: "#059669"}, {
                                        label: "District Avg",
                                        c: "#2563EB"
                                    }, {label: "Bottom School", c: "#DC2626"}].map(l => (
                                        <div key={l.label} style={{display: "flex", alignItems: "center", gap: "4px"}}>
                                            <div style={{
                                                width: "10px",
                                                height: "3px",
                                                borderRadius: "2px",
                                                background: l.c
                                            }}/>
                                            <span style={{
                                                fontSize: "10px",
                                                color: "#64748B",
                                                fontWeight: 600
                                            }}>{l.label}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div style={{
                                background: "#FFF",
                                borderRadius: "10px",
                                border: "1px solid #E2E8F0",
                                padding: "18px 14px 10px 0"
                            }}>
                                <div style={{
                                    fontSize: "11px",
                                    fontWeight: 700,
                                    color: "#64748B",
                                    textTransform: "uppercase",
                                    letterSpacing: "0.8px",
                                    marginBottom: "6px",
                                    paddingLeft: "18px"
                                }}>
                                    Grade Band Progression
                                </div>
                                <ResponsiveContainer width="100%" height={240}>
                                    <BarChart data={GRADE_BAND} margin={{top: 5, right: 10, bottom: 5, left: 10}}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9"/>
                                        <XAxis dataKey="grade" tick={{fontSize: 11, fill: "#64748B"}}/>
                                        <YAxis domain={[0, 100]} tick={{fontSize: 11, fill: "#64748B"}}
                                               tickFormatter={v => `${v}%`}/>
                                        <Tooltip content={<CustomTooltip/>}/>
                                        <ReferenceLine y={PROF} stroke="#94A3B8" strokeDasharray="6 4"/>
                                        <Bar dataKey="reason" fill="#2563EB" name="Reasoning" radius={[3, 3, 0, 0]}/>
                                        <Bar dataKey="construct_arguments" fill="#059669" name="Arguments"
                                             radius={[3, 3, 0, 0]}/>
                                        <Bar dataKey="precision" fill="#D97706" name="Precision" radius={[3, 3, 0, 0]}/>
                                        <Bar dataKey="model_analyze" fill="#7C3AED" name="Model" radius={[3, 3, 0, 0]}/>
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    </>
                )}

                {/* ════════ SCHOOLS ════════ */}
                {section === "schools" && (
                    <>
                        <div style={{
                            display: "grid", gridTemplateColumns: "200px 1fr 60px 60px 60px 20px",
                            gap: "14px", padding: "0 18px 8px", fontSize: "9px", fontWeight: 700,
                            color: "#94A3B8", textTransform: "uppercase", letterSpacing: "0.8px",
                        }}>
                            <span>School</span><span></span>
                            <span style={{textAlign: "center"}}>Avg</span>
                            <span style={{textAlign: "center"}}>Growth</span>
                            <span style={{textAlign: "center"}}>Prof %</span>
                            <span></span>
                        </div>
                        <div style={{display: "flex", flexDirection: "column", gap: "8px"}}>
                            {[...SCHOOLS].sort((a, b) => {
                                const aAvg = SK_KEYS.reduce((s, k) => s + a.growth[k], 0) / SK_KEYS.length;
                                const bAvg = SK_KEYS.reduce((s, k) => s + b.growth[k], 0) / SK_KEYS.length;
                                return aAvg - bAvg;
                            }).map(school => (
                                <SchoolRow key={school.id} school={school} districtAvg={districtAvg}
                                           expanded={expandedSchool === school.id}
                                           onToggle={() => setExpandedSchool(expandedSchool === school.id ? null : school.id)}/>
                            ))}
                        </div>
                    </>
                )}

                {/* ════════ EQUITY ════════ */}
                {section === "equity" && (
                    <>
                        <div style={{
                            background: "#FFF",
                            borderRadius: "10px",
                            border: "1px solid #E2E8F0",
                            padding: "20px 16px 10px 0",
                            marginBottom: "16px"
                        }}>
                            <div style={{
                                fontSize: "11px",
                                fontWeight: 700,
                                color: "#64748B",
                                textTransform: "uppercase",
                                letterSpacing: "0.8px",
                                marginBottom: "10px",
                                paddingLeft: "20px"
                            }}>
                                Skill Averages by Student Group
                            </div>
                            <ResponsiveContainer width="100%" height={260}>
                                <BarChart data={EQUITY_DATA} margin={{top: 5, right: 20, bottom: 5, left: 10}}
                                          layout="vertical">
                                    <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9"/>
                                    <XAxis type="number" domain={[0, 100]} tick={{fontSize: 11, fill: "#64748B"}}
                                           tickFormatter={v => `${v}%`}/>
                                    <YAxis type="category" dataKey="group" tick={{fontSize: 11, fill: "#374151"}}
                                           width={100}/>
                                    <Tooltip content={<CustomTooltip/>}/>
                                    <ReferenceLine x={PROF} stroke="#94A3B8" strokeDasharray="6 4"/>
                                    <Bar dataKey="reason" fill="#2563EB" name="Reasoning" radius={[0, 3, 3, 0]}
                                         barSize={12}/>
                                    <Bar dataKey="construct_arguments" fill="#059669" name="Arguments"
                                         radius={[0, 3, 3, 0]} barSize={12}/>
                                    <Bar dataKey="precision" fill="#D97706" name="Precision" radius={[0, 3, 3, 0]}
                                         barSize={12}/>
                                    <Bar dataKey="model_analyze" fill="#7C3AED" name="Model" radius={[0, 3, 3, 0]}
                                         barSize={12}/>
                                </BarChart>
                            </ResponsiveContainer>
                        </div>

                        {/* Gap analysis */}
                        <div style={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr",
                            gap: "10px",
                            marginBottom: "16px"
                        }}>
                            {["reason", "construct_arguments", "precision", "model_analyze"].map(sk => {
                                const allAvg = EQUITY_DATA[0][sk];
                                const t1 = EQUITY_DATA[1][sk];
                                const nonT1 = EQUITY_DATA[2][sk];
                                const ell = EQUITY_DATA[3][sk];
                                const sped = EQUITY_DATA[4][sk];
                                const maxGap = Math.max(allAvg - t1, allAvg - ell, allAvg - sped);
                                return (
                                    <div key={sk} style={{
                                        padding: "14px 16px", background: "#FFF", borderRadius: "8px",
                                        border: "1px solid #E2E8F0", borderLeft: `4px solid ${SKILLS[sk].color}`,
                                    }}>
                                        <div style={{
                                            fontSize: "12px",
                                            fontWeight: 700,
                                            color: "#111827",
                                            marginBottom: "8px"
                                        }}>{SKILLS[sk].name}</div>
                                        <div style={{display: "flex", flexDirection: "column", gap: "4px"}}>
                                            {[
                                                {label: "Title I Gap", val: allAvg - t1},
                                                {label: "ELL Gap", val: allAvg - ell},
                                                {label: "SpEd Gap", val: allAvg - sped},
                                            ].map(g => (
                                                <div key={g.label} style={{
                                                    display: "flex",
                                                    justifyContent: "space-between",
                                                    alignItems: "center"
                                                }}>
                                                    <span style={{fontSize: "11px", color: "#64748B"}}>{g.label}</span>
                                                    <span style={{
                                                        fontSize: "12px", fontWeight: 700,
                                                        color: g.val > 15 ? "#DC2626" : g.val > 8 ? "#D97706" : "#059669",
                                                    }}>{g.val} pts</span>
                                                </div>
                                            ))}
                                        </div>
                                        <div style={{
                                            marginTop: "8px", fontSize: "10px", fontWeight: 600, padding: "4px 8px",
                                            borderRadius: "4px",
                                            background: maxGap > 15 ? "#FEF2F2" : maxGap > 8 ? "#FFFBEB" : "#ECFDF5",
                                            color: maxGap > 15 ? "#991B1B" : maxGap > 8 ? "#92400E" : "#065F46",
                                        }}>
                                            Largest gap: {maxGap} pts
                                            — {maxGap > 15 ? "Critical" : maxGap > 8 ? "Monitor" : "Acceptable"}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </>
                )}

                {/* ════════ STRATEGY ════════ */}
                {section === "strategy" && (
                    <div style={{display: "flex", flexDirection: "column", gap: "12px"}}>
                        {STRATEGIC.map((rec, i) => (
                            <div key={i} style={{
                                padding: "20px 22px", background: "#FFF", borderRadius: "12px",
                                border: "1px solid #E2E8F0", borderLeft: `5px solid ${rec.color}`,
                                boxShadow: rec.priority === "critical" ? "0 2px 12px rgba(0,0,0,0.06)" : "none",
                            }}>
                                <div style={{display: "flex", alignItems: "center", gap: "10px", marginBottom: "10px"}}>
                  <span style={{
                      fontSize: "9px",
                      fontWeight: 800,
                      padding: "3px 10px",
                      borderRadius: "99px",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      background: rec.priority === "critical" ? "#FEE2E2" : rec.priority === "high" ? "#FEF3C7" : "#DBEAFE",
                      color: rec.priority === "critical" ? "#991B1B" : rec.priority === "high" ? "#92400E" : "#1E40AF",
                  }}>{rec.priority}</span>
                                    <span style={{
                                        fontSize: "14px",
                                        fontWeight: 700,
                                        color: "#0F172A",
                                        flex: 1
                                    }}>{rec.title}</span>
                                </div>

                                <div style={{
                                    display: "inline-block",
                                    padding: "4px 12px",
                                    borderRadius: "5px",
                                    background: "#F1F5F9",
                                    fontSize: "11px",
                                    fontWeight: 700,
                                    color: "#475569",
                                    marginBottom: "10px",
                                }}>{rec.metric}</div>

                                <p style={{
                                    margin: "0 0 12px",
                                    fontSize: "13px",
                                    color: "#1E293B",
                                    lineHeight: 1.7
                                }}>{rec.insight}</p>

                                <div style={{
                                    padding: "12px 16px", borderRadius: "8px", background: "#FFFBEB",
                                    border: "1px solid #FDE68A", marginBottom: "10px",
                                }}>
                                    <div style={{
                                        fontSize: "10px",
                                        fontWeight: 700,
                                        color: "#92400E",
                                        textTransform: "uppercase",
                                        letterSpacing: "0.5px",
                                        marginBottom: "4px"
                                    }}>Recommended Action
                                    </div>
                                    <p style={{
                                        margin: 0,
                                        fontSize: "12px",
                                        color: "#78350F",
                                        lineHeight: 1.6
                                    }}>{rec.action}</p>
                                </div>

                                <div style={{
                                    padding: "10px 14px", borderRadius: "8px",
                                    background: "#F0FDF4", border: "1px solid #BBF7D0",
                                }}>
                                    <div style={{
                                        fontSize: "10px",
                                        fontWeight: 700,
                                        color: "#166534",
                                        textTransform: "uppercase",
                                        letterSpacing: "0.5px",
                                        marginBottom: "3px"
                                    }}>Budget Signal
                                    </div>
                                    <p style={{
                                        margin: 0,
                                        fontSize: "12px",
                                        color: "#14532D",
                                        lineHeight: 1.5
                                    }}>{rec.budget_signal}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* JSON */}
                {showJson && (
                    <div style={{marginTop: "18px"}}>
                        <div style={{
                            fontSize: "11px",
                            fontWeight: 700,
                            color: "#64748B",
                            textTransform: "uppercase",
                            letterSpacing: "0.8px",
                            marginBottom: "6px"
                        }}>
                            District API Response
                        </div>
                        <pre style={{
                            background: "#0F172A", color: "#CBD5E1", padding: "18px",
                            borderRadius: "10px", fontSize: "11px", lineHeight: 1.5,
                            overflow: "auto", maxHeight: "500px", margin: 0,
                            fontFamily: "'SF Mono', 'Fira Code', monospace",
                        }}>
              {JSON.stringify(jsonOutput, null, 2)}
            </pre>
                    </div>
                )}
            </div>
        </div>
    );
}
